import React, { useState, useEffect } from 'react';

const CompanySearchResults = ({ 
  showCompanyResults, 
  companyResults, 
  handleCompanySelect, 
  input, 
  resetSearch,
  isCollapsed,
  setIsCollapsed
}) => {
  const [animatedCards, setAnimatedCards] = useState([]);

  // Reset collapse state when new search results come in
  useEffect(() => {
    if (showCompanyResults && companyResults.length > 0) {
      setIsCollapsed(false);
      setAnimatedCards([]);
      
      // Animate cards one by one
      companyResults.forEach((_, index) => {
        setTimeout(() => {
          setAnimatedCards(prev => [...prev, index]);
        }, index * 100); // 100ms delay between each card
      });
    }
  }, [showCompanyResults, companyResults, setIsCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (!showCompanyResults) return null;

  return (
    <div className="max-w-7xl mx-auto my-12 p-8 animate-slide-up backdrop-blur-sm">
      {companyResults.length > 0 ? (
        <>
          <div 
            className="flex items-center justify-between cursor-pointer group mb-6"
            onClick={toggleCollapse}
          >
            <div>
              <h2 className="text-3xl font-bold text-yellow-400 flex items-center">
                Found {companyResults.length} {companyResults.length === 1 ? 'Company' : 'Companies'}
              </h2>
              {!isCollapsed && (
                <p className="text-gray-300 mt-2">Select a company to find its decision makers:</p>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-yellow-400 text-sm font-medium group-hover:text-yellow-300 transition-colors">
                {isCollapsed ? 'Show Companies' : 'Hide Companies'}
              </span>
              <div className={`transform transition-transform duration-200 text-yellow-400 text-xl ${
                isCollapsed ? 'rotate-180' : 'rotate-0'
              }`}>
                ⌄
              </div>
            </div>
          </div>
          
          {!isCollapsed && (
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              {companyResults.map((company, index) => (
                <div
                  key={company.id || index}
                  onClick={() => handleCompanySelect(company)}
                  className={`bg-black/30 rounded-2xl p-3 lg:p-6 border border-yellow-500/10 hover:border-yellow-500/30 transition-all duration-300 cursor-pointer hover:bg-gradient-to-r hover:from-yellow-500/5 hover:to-yellow-600/5 group transform ${
                    animatedCards.includes(index) 
                      ? 'translate-y-0 opacity-100' 
                      : 'translate-y-8 opacity-0'
                  }`}
                  style={{
                    transition: 'all 0.5s ease-out',
                    transitionDelay: animatedCards.includes(index) ? '0ms' : `${index * 100}ms`
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg lg:text-2xl font-bold text-yellow-400 mb-1 lg:mb-2 group-hover:text-yellow-300 transition-colors duration-200">
                        {company.name}
                      </h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 lg:gap-4 text-sm lg:text-base">
                        {company.industry && (
                          <div>
                            <span className="text-gray-400">Industry:</span>
                            <p className="text-white font-medium">{company.industry}</p>
                          </div>
                        )}
                        {company.region && (
                          <div>
                            <span className="text-gray-400">Region:</span>
                            <p className="text-white font-medium">{company.region}</p>
                          </div>
                        )}
                        {company.email_domain && (
                          <div>
                            <span className="text-gray-400">Email Domain:</span>
                            <p className="text-white font-medium">{company.email_domain}</p>
                          </div>
                        )}
                        {company.id && (
                          <div>
                            <span className="text-gray-400">Company ID:</span>
                            <p className="text-white font-medium">{company.id}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex items-center text-yellow-500 group-hover:text-yellow-400 transition-colors duration-200">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          <div className="text-yellow-500 text-4xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-yellow-400 mb-4">No Companies Found</h2>
          <p className="text-gray-300 text-lg mb-6">
            We couldn't find any companies matching "{input}". Try:
          </p>
          <ul className="text-gray-400 text-left max-w-md mx-auto space-y-2">
            <li>• Checking the spelling</li>
            <li>• Using the official company name</li>
            <li>• Trying a shorter version of the name</li>
            <li>• Searching for a parent company</li>
          </ul>
          <button
            onClick={resetSearch}
            className="mt-6 px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black rounded-xl hover:from-yellow-400 hover:to-yellow-500 transition-all duration-200 font-semibold"
          >
            Try Another Search
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanySearchResults;